import { useNavigate } from "react-router-dom";
import { FiLayers, FiGrid, FiCompass } from "react-icons/fi";
import { ScrollReveal } from "./ui/ScrollReveal";

const GuestView = () => {
  const navigate = useNavigate();
  
  const featuredSectors = [
    {
      icon: <FiLayers className="w-8 h-8" />,
      title: "Healthcare",
      description: "Discover AI applications in medical diagnosis, patient care, and healthcare management",
      color: "from-blue-500 to-cyan-400"
    },
    {
      icon: <FiGrid className="w-8 h-8" />,
      title: "Manufacturing",
      description: "Explore AI solutions for production optimization, quality control, and predictive maintenance",
      color: "from-orange-500 to-amber-400"
    },
    {
      icon: <FiCompass className="w-8 h-8" />,
      title: "Finance",
      description: "Find AI use cases in risk assessment, fraud detection, and automated trading",
      color: "from-green-500 to-emerald-400"
    }
  ];

  return (
    <div
      className="w-full min-h-screen flex flex-col items-center pt-24 px-4 md:px-12 lg:px-24 relative overflow-hidden"
      style={{
        background:
          "radial-gradient(25% 20% at 50% 0%, rgba(255, 84, 31, 0.5) 0%, rgba(0, 0, 0, 0) 100%), black",
        color: "white",
      }}
    >
      {/* Mobile-only tweaks */}
      <style>{`
        @media (max-width: 640px) {
          .bg-dots { display: none !important; }
          .hero-title { font-size: 32px !important; letter-spacing: -0.5px !important; }
          .hero-title .accent, .accent { font-size: 32px !important; }
          .hero-subtitle { font-size: 15px !important; padding: 12px 16px !important; margin: 8px auto 12px !important; }
          .cta-container { flex-direction: column !important; gap: 10px !important; width: 100% !important; padding-left: 8px !important; padding-right: 8px !important; }
          .cta-btn { width: 100% !important; height: 48px !important; font-size: 16px !important; padding-left: 20px !important; padding-right: 20px !important; }
          .section-title { font-size: 22px !important; margin-bottom: 12px !important; }
          .sector-card { padding: 14px !important; }
          .sector-title { font-size: 18px !important; }
          .sector-desc { font-size: 13px !important; line-height: 1.4 !important; }
          .why-title { font-size: 30px !important; }
          .feature-card { padding: 14px !important; }
          .feature-card h3 { font-size: 16px !important; margin-bottom: 10px !important; }
          .feature-card p { font-size: 14px !important; line-height: 20px !important; }
        }
      `}</style>

      {/* Dots Pattern Background */}
      <div
        className="absolute bg-dots"
        style={{
          width: "1106px",
          height: "500px",
          top: "-12px",
          left: "194px",
          backgroundImage: "url('/dots.png')",
          backgroundSize: "contain",
          backgroundRepeat: "no-repeat",
          opacity: 1,
          pointerEvents: "none",
          userSelect: "none",
          zIndex: 0,
        }}
      />

      {/* Hero Section */}
      <div className="w-full flex flex-col items-center justify-center px-4 text-center pt-12">
        <ScrollReveal>
          <h1 className="mb-2 hero-title" style={{ 
            fontFamily: 'IBM Plex Sans',
            fontWeight: 700,
            fontSize: '48px',
            lineHeight: '110%',
            letterSpacing: '-1px',
            textAlign: 'center'
          }}>
            Explore AI Use Cases
            <br />
            <span className="accent" style={{ 
              fontFamily: 'IBM Plex Sans',
              fontWeight: 700,
              fontSize: '48px',
              lineHeight: '110%',
              letterSpacing: '-1px',
              color: '#FF541F',
              background: 'none',
              WebkitBackgroundClip: 'unset',
              WebkitTextFillColor: 'unset'
            }}>
              Across Sectors & Sub-Sectors
            </span>
          </h1>
        </ScrollReveal>
        
        <ScrollReveal delay={0.2}>
          <p
            className="hero-subtitle"
            style={{
              fontFamily: 'Lora',
              fontWeight: 400,
              fontSize: '18px',
              lineHeight: '150%',
              letterSpacing: '0',
              textAlign: 'center',
              color: '#9CA3AF',
              borderRadius: '12px',
              maxWidth: '600px',
              margin: '10px auto',
              padding: '16px 24px',
              boxShadow: 'none',
              backdropFilter: 'none',
            }}
          >
          Instantly discover real-world use cases across sectors and sub-sectors. Search, compare, and explore innovative solutions transforming industries—find inspiration and actionable ideas for your business, research, or projects.
          </p>
        </ScrollReveal>

        <ScrollReveal delay={0.4}>
          <div className="flex gap-4 mb-10 justify-center cta-container">
            <button
              onClick={() => navigate("/login")}
              className="text-white font-semibold rounded-md shadow transition duration-200 text-base cta-btn"
              style={{
                width: '179px',
                height: '50px',
                borderRadius: '10px',
                border: '1px solid #FCFCFC3B',
                padding: '15px 35px',
                backgroundColor: '#FF541F',
              }}
            >
              Get Started
            </button>
            <button
              onClick={() => navigate("/about-us")}
              className="text-white font-semibold rounded-md transition duration-200 text-base cta-btn"
              style={{
                width: '179px',
                height: '50px',
                borderRadius: '10px',
                border: '1px solid #FCFCFC3B',
                padding: '15px 35px',
                backgroundColor: '#181818',
                fontFamily: 'Lora',
                letterSpacing: '0%',
                verticalAlign: 'middle',
                color: '#FFFFFF',
              }}
            >
              Learn More
            </button>
          </div>
        </ScrollReveal>
      </div>

      {/* Featured Sectors */}
      <div className="w-full py-16">
        <div className="mx-auto px-4">
          <ScrollReveal>
            <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center section-title">
              Featured Sectors
            </h2>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {featuredSectors.map((sector, index) => (
              <ScrollReveal key={index} delay={index * 0.2}>
                <div className="bg-[#181818] p-6 rounded-xl border border-gray-800 hover:shadow-2xl transition-all duration-300 sector-card">
                  {/* Removed gradient background; icon stays white on dark card */}
                  <div className="w-16 h-16 rounded-lg mb-4 flex items-center justify-center">
                    {sector.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-2 text-white sector-title">{sector.title}</h3>
                  <p className="text-gray-300 sector-desc">{sector.description}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </div>

      {/* Why Choose Our Platform */}
      <div className="w-full py-16">
        <div className="max-w-6xl mx-auto">
          <ScrollReveal>
            <div className="text-center mb-16">
              <h2 className="text-2xl md:text-3xl font-bold mb-8 text-white why-title">
                Why Choose Our Platform?
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="p-6 bg-[#181818] rounded-xl border border-gray-800 shadow feature-card">
                  <h3 className="text-xl font-bold mb-2 text-white">Sector-Specific Focus</h3>
                  <p className="text-gray-300">Tailored AI solutions for your industry's unique challenges</p>
                </div>
                <div className="p-6 bg-[#181818] rounded-xl border border-gray-800 shadow feature-card">
                  <h3 className="text-xl font-bold mb-2 text-white">Intelligent Discovery</h3>
                  <p className="text-gray-300">Advanced search and recommendations powered by AI</p>
                </div>
                <div className="p-6 bg-[#181818] rounded-xl border border-gray-800 shadow feature-card">
                  <h3 className="text-xl font-bold mb-2 text-white">Implementation Guides</h3>
                  <p className="text-gray-300">Detailed resources to help you succeed with AI</p>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </div>
  );
};

export default GuestView;